import { readonly, ref, type Ref } from 'vue'

export type ColorScheme = 'light' | 'dark'

/** localStorage key of the chosen scheme (beside `skileo.session`). Nothing stored = light, whatever the OS prefers. */
export const COLOR_SCHEME_KEY = 'skileo.theme'
/** PrimeVue's `darkModeSelector` (theme/index.ts), set on `<html>`. */
export const DARK_CLASS = 'skileo-dark'

const scheme = ref<ColorScheme>('light')

/** The stored choice; light when nothing is stored or storage is unavailable. */
export function readStoredScheme(): ColorScheme {
  try {
    return localStorage.getItem(COLOR_SCHEME_KEY) === 'dark' ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

/**
 * Switches the document to `next`: the dark class on `<html>` flips PrimeVue's tokens (and everything reading
 * `--p-*`), `color-scheme` flips the browser's own chrome (scrollbars, form controls). Persisted unless told not to
 * (boot re-applies the stored choice without writing it back).
 */
export function applyColorScheme(next: ColorScheme, { persist = true } = {}): void {
  scheme.value = next
  const root = document.documentElement
  root.classList.toggle(DARK_CLASS, next === 'dark')
  root.style.colorScheme = next
  if (!persist) return
  try {
    localStorage.setItem(COLOR_SCHEME_KEY, next)
  } catch {
    // Private mode or a full store: the choice lasts for the session.
  }
}

/** The current scheme and its toggle (the navbar's switch). */
export function useColorScheme(): { scheme: Readonly<Ref<ColorScheme>>; toggle: () => void } {
  return {
    scheme: readonly(scheme),
    toggle: () => applyColorScheme(scheme.value === 'dark' ? 'light' : 'dark'),
  }
}
